/*
 * The copilot flyout — §13 E1/E2, and D-07 as §13.1 amends it.
 *
 * ── One component, four routes ─────────────────────────────────────────────────────────────────
 *
 * The screen declares what it is about (`useCopilotTarget`), and this component asks whichever
 * module owns that kind. `modules/clients/src/routes/copilot-ask.ts` explains why the server side
 * cannot be one route; the browser is where the four are composed back into one thing a person uses,
 * which is the arrangement every screen in this product already has.
 *
 * ── Summoned, and pinned only if somebody asks ─────────────────────────────────────────────────
 *
 * D-07 chose a persistent rail and the shell charged 22rem for it on every screen, for a component
 * that did not exist. §13.1 makes the rail the *pinned state* of a flyout: closed by default, opened
 * with the header button or `[`, docked into the shell's reserved region when pinned. Pinning is a
 * preference (`copilot_pinned`) rather than session state, because it is exactly the sort of thing
 * two people doing the same job disagree about, and asking again every morning is worse than either
 * answer.
 *
 * ── It writes nothing, and says so ─────────────────────────────────────────────────────────────
 *
 * §13's v1 reads everything and writes nothing. There is no "apply this", no "save to the record",
 * and the footer says as much in words rather than leaving people to discover it. The seam for
 * later — pinning an answer onto a record — is a route that does not exist yet; nothing here has to
 * change for it, which is why the conversation being ephemeral was the safe call rather than the
 * cheap one.
 *
 * ── Two layers, chosen by hand — M-17, 31 Aug 2026 ────────────────────────────────────────────
 *
 * **Record** asks about the subject on screen, through whichever of the four routes owns that kind.
 * **Book** asks about the reader's book of work, through `insight.copilot.ask_book`, which
 * interprets the question into a plan of read-only steps and answers from what they returned.
 *
 * **An explicit toggle, and no classifier.** A question routed upward by mistake is not a worse
 * answer, it is a *disclosure*: Record is bounded by the record in front of somebody, and Book
 * reaches across their whole book of work. A guess about which one somebody meant is a guess about
 * how much to open, and that is not a guess worth making to save one click.
 *
 * The toggle is per turn rather than per session, and the transcript records which layer answered
 * — two answers in one conversation can legitimately come from different layers, and a reader
 * comparing them needs to know which was which.
 *
 * ── The conversation is a transcript, not a thread ─────────────────────────────────────────────
 *
 * Turns are kept in component state for as long as the flyout is open, so a person can read what
 * they asked two questions ago. Nothing is sent back with the next question: each ask is answered
 * from the record alone, and a model that saw its own previous answers would start citing them.
 * When the flyout closes, the transcript goes — which is what "ephemeral" means here, and it is
 * visible in the copy rather than hidden in an implementation.
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button, ProvenanceMark, Spinner, Textarea } from '@indovation/design-system';
import { useI18n } from '@indovation/i18n/react';
import type { MessageKey } from '@indovation/i18n';
import { useSession } from '../auth/session.js';
import { useCopilotContext, useCopilotSession } from './context.js';
import { STEP_SCREENS } from './step-screens.js';
import { useScope } from '../scope/scope.js';
import type { QueryScope } from '../scope/scope.js';
import { SetName } from '../app/set-name.js';
import type { CopilotTarget } from './context.js';

/** One exchange, held for as long as the flyout is open. */
interface Turn {
  readonly id: number;
  readonly question: string;
  readonly reply: string;
  readonly citations: readonly string[];
  readonly refused: boolean;
  /** Which backend answered — see ProvenanceMark. Held per turn, not per flyout: routing is
      per feature and per subject, so two answers in one session can legitimately differ. */
  readonly sample: boolean;
  /** Which layer produced it. Two answers in one transcript can come from different layers. */
  readonly layer: Layer;
  /**
   * What Book understood, when Book answered — nl-voice-search §2's trust mechanism.
   *
   * Shown as chips above the reply so a reader can see what was looked at before reading what was
   * concluded. A misreading is then fixed by rephrasing one thing rather than the whole question,
   * and a citation points at something already on the screen. **Editing the chips is a later
   * slice; showing them is not.**
   */
  readonly plan?: {
    readonly steps: readonly {
      kind: string;
      /** Internal, and used for exactly one thing: choosing the screen a chip links to. */
      id: string;
      label: string;
      state: string;
      found: number;
    }[];
    readonly residue: string | null;
    readonly confidence: number;
  };
}

/** The two layers, named as the product owner named them. */
type Layer = 'record' | 'book';

/**
 * Three prompts per kind of screen — §13.3, and static on purpose.
 *
 * Generated suggestions would be a model call to decide what to ask a model, on every screen change,
 * before anybody has asked for anything. These are the three questions people actually open the
 * flyout with, and being fixed is what makes them learnable: the same three in the same order every
 * time becomes muscle memory, where a rotating set is read from scratch each visit.
 */
const PROMPTS: Record<CopilotTarget['kind'], readonly MessageKey[]> = {
  job: ['copilot.prompt.job.1', 'copilot.prompt.job.2', 'copilot.prompt.job.3'],
  candidate: ['copilot.prompt.candidate.1', 'copilot.prompt.candidate.2', 'copilot.prompt.candidate.3'],
  application: [
    'copilot.prompt.application.1',
    'copilot.prompt.application.2',
    'copilot.prompt.application.3',
  ],
  desk: ['copilot.prompt.desk.1', 'copilot.prompt.desk.2', 'copilot.prompt.desk.3'],
};

/**
 * What bounds a Book answer, said in the shell's own words.
 *
 * The same three the scope control offers, and the same catalogue keys, so the two cannot end up
 * describing the same setting differently — the panel saying "Mine" while the header says
 * "The agency" is a worse confusion than the one this replaces.
 */
const SCOPE_LABELS: Record<QueryScope, MessageKey> = {
  all: 'scope.option.all',
  team: 'scope.option.team',
  own: 'scope.option.own',
};

const KIND_LABELS: Record<CopilotTarget['kind'], MessageKey> = {
  job: 'copilot.kind.job',
  candidate: 'copilot.kind.candidate',
  application: 'copilot.kind.application',
  desk: 'copilot.kind.desk',
};

export interface FlyoutProps {
  readonly pinned: boolean;
  readonly onTogglePin: () => void;
  readonly onClose: () => void;
}

export default function CopilotFlyout({ pinned, onTogglePin, onClose }: FlyoutProps) {
  const { t } = useI18n();
  const { api } = useSession();
  const target = useCopilotContext();
  const session = useCopilotSession();
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [layer, setLayer] = useState<Layer>('record');
  const nextId = useRef(1);
  /*
   * The shell's current scope, sent with every Book ask so Agency/Team/Mine governs the answer
   * exactly as it governs every list. Record does not take one: it is already bounded by the one
   * record it is about, and a scope on a single subject would be a control with no effect.
   */
  const { scope } = useScope();

  const ask = useMutation({
    mutationFn: async (asked: string) => {
      if (layer === 'book') {
        return api.call('insight.copilot.ask_book', {
          body: {
            question: asked,
            scope,
            // What is on screen, as a hint the interpreter may narrow with — never a filter applied
            // here. The narrowing then shows up as a step the reader can see, rather than as a
            // condition nobody was told about.
            ...(target.kind === 'job' ? { subjectId: target.id } : {}),
          },
        });
      }

      /*
       * The one place the four routes are chosen between.
       *
       * A lookup table keyed on kind rather than a chain of ifs, so a new kind is a compile error
       * here rather than a silent fall-through to the desk — which would answer confidently about
       * the wrong thing, the failure mode this whole design is arranged to avoid.
       */
      switch (target.kind) {
        case 'job':
          return api.call('clients.copilot.ask', {
            params: { jobId: target.id },
            body: { question: asked },
          });
        case 'candidate':
          return api.call('candidates.copilot.ask', {
            params: { candidateId: target.id },
            body: { question: asked },
          });
        case 'application':
          return api.call('pipeline.copilot.ask', {
            params: { applicationId: target.id },
            body: { question: asked },
          });
        case 'desk':
          return api.call('insight.copilot.ask', { body: { question: asked } });
      }
    },
    onSuccess: (answer, asked) => {
      setTurns((was) => [
        ...was,
        {
          id: nextId.current++,
          question: asked,
          reply: answer.reply,
          citations: answer.citations,
          refused: answer.refused,
          sample: answer.ai_sample,
          layer,
          ...('plan' in answer ? { plan: answer.plan } : {}),
        },
      ]);
      setQuestion('');
    },
  });

  /*
   * §13 E5: a question handed in from somewhere else on the page.
   *
   * Keyed on the nonce rather than on the text, so "discuss this" pressed twice on the same
   * artifact asks twice. The pending question is cleared as it is consumed, which is what stops it
   * being re-asked every time this component re-renders for an unrelated reason.
   */
  useEffect(() => {
    if (!session.pending) return;
    const asked = session.pending.question;
    session.clearPending();
    if (asked.trim().length >= 3) ask.mutate(asked.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the nonce is the event.
  }, [session.pending?.nonce]);

  const send = (asked: string) => {
    const trimmed = asked.trim();
    if (trimmed.length < 3 || ask.isPending) return;
    ask.mutate(trimmed);
  };

  return (
    <section className="tf-copilot" aria-label={t('copilot.title')}>
      <header className="tf-copilot__head">
        {/*
          The panel's own name, set as a name rather than typed — the one place in this panel where
          the word is the assistant rather than a word about it. `aria-label` on the section above
          already carries the plain string, so the mark inside is not announced twice.
        */}
        <strong>
          <SetName text={t('copilot.title')} />
        </strong>

        {/*
          The layer switch, in the header — 31 Aug 2026.

          It began as two stacked cards below the context row, each with a title and an explanatory
          line, and on a 22rem rail that was roughly a fifth of the panel spent on a control with
          two states. The explanation moved to where it is actually needed: the ask box's
          placeholder says what this layer will read, so it is in front of somebody at the moment
          they are deciding what to type rather than above a transcript they have stopped looking
          at. The note survives as the button's own accessible description for anyone who wants it
          spelled out.

          Still two buttons rather than a select, and still no classifier: see the header. A
          segmented pair shows both states at once, which is what makes "which one is answering"
          readable without an interaction.
        */}
        <div className="tf-copilot__layers" role="group" aria-label={t('copilot.layer.choose')}>
          {(['record', 'book'] as const).map((one) => (
            <button
              key={one}
              type="button"
              className={layer === one ? 'tf-copilot__layer is-on' : 'tf-copilot__layer'}
              aria-pressed={layer === one}
              aria-description={t(`copilot.layer.${one}Note` as MessageKey)}
              title={t(`copilot.layer.${one}Note` as MessageKey)}
              onClick={() => setLayer(one)}
            >
              {t(`copilot.layer.${one}` as MessageKey)}
            </button>
          ))}
        </div>

        <span className="tf-copilot__controls">
          <button
            type="button"
            className="tf-copilot__dock"
            aria-pressed={pinned}
            onClick={onTogglePin}
          >
            {t(pinned ? 'copilot.unpin' : 'copilot.pin')}
          </button>
          {/*
            Close is the one control in this header that needs no word: a cross in the corner of a
            panel is understood everywhere, and the two words it replaces were competing for the
            same row as the layer switch. The label is still there for anybody not reading pixels —
            `aria-label` carries it, and the glyph is hidden from assistive technology so the
            control is announced once rather than as "✕ Close".
          */}
          <button
            type="button"
            className="tf-copilot__close"
            aria-label={t('copilot.close')}
            onClick={onClose}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </span>
      </header>

      {/*
        §13.3's context chip. It says what will be *read*, not what is on screen — those are the
        same thing when a screen has declared itself and differ when it has not, and the difference
        is the one thing somebody needs to know before trusting an answer.
      */}
      <div className="tf-copilot__context">
        {layer === 'book' ? (
          /*
             In Book, the context row says what Book will read — 31 Aug 2026.

             It said what was on screen, in both layers, and on a candidate record that read
             *"Candidate · Pushpa Naik"* above an answer that had searched every candidate the
             reader can see. Nothing was disclosed that a click would not have shown — every step
             runs on the caller's own transaction, under their entitlement, policy and RLS — but the
             panel had told them the answer was about one person and then answered about their book.
             *"Is that a leak?"* is the correct question to ask a screen that says that, and the
             screen was what was wrong.

             So the chip names the layer and the subject names the **scope**, which is the thing
             that actually bounds a Book answer. A requisition on screen is still passed as a hint
             and still shows as a step, so the narrowing stays visible where narrowing happens.
          */
          <>
            <span className="tf-chip">{t('copilot.kind.book')}</span>
            <span className="tf-copilot__subject">{t(SCOPE_LABELS[scope])}</span>
          </>
        ) : (
          <>
            <span className="tf-chip">{t(KIND_LABELS[target.kind])}</span>
            <span className="tf-copilot__subject">
              {target.kind === 'desk' ? t('copilot.kind.deskSubject') : target.label}
            </span>
            {target.kind === 'job' ? (
              <Link to="/jobs/$jobId/workbench" params={{ jobId: target.id }} className="tf-link">
                {t('copilot.openWorkbench')}
              </Link>
            ) : null}
          </>
        )}
      </div>

      <div className="tf-copilot__transcript">
        {turns.length === 0 ? (
          <div className="tf-copilot__suggestions">
            <p className="tf-muted">{t('copilot.suggestions')}</p>
            {PROMPTS[target.kind].map((key) => (
              <button
                key={key}
                type="button"
                className="tf-copilot__suggestion"
                onClick={() => send(t(key))}
              >
                {t(key)}
              </button>
            ))}
          </div>
        ) : null}

        {turns.map((turn) => (
          <article key={turn.id} className="tf-copilot__turn">
            {/*
              Asked on one side, answered on the other — 31 Aug 2026.

              A transcript of alternating paragraphs in one column reads as a document, and a person
              scanning back through five turns has to read the words to find out which are theirs.
              The question sits on the end side and the answer on the start side, which is where
              every messaging surface puts them, and it is recognised before it is read. The
              question keeps the quieter shade: it is the thing somebody already knows they typed.
            */}
            <p className="tf-copilot__question">{turn.question}</p>
            <div className="tf-copilot__answer">
            <ProvenanceMark
              sample={turn.sample}
              labels={{
                sample: t('ai.sample'),
                answeredBy: t('ai.answeredBy', { assistant: t('ai.assistantName') }),
              }}
            />
            {/*
              What Book understood, before what it concluded. Deliberately above the answer: a
              reader who sees "interviews by outcome, last 14 days" and meant something else has
              stopped reading a wrong answer one line in, rather than at the end of a paragraph
              they had already half believed.
            */}
            {turn.plan ? (
              <div className="tf-copilot__plan">
                {turn.plan.steps.map((step) => {
                  const screen = STEP_SCREENS[step.id];
                  /*
                   * The server's name, not one derived here from the id.
                   *
                   * This used to strip the module prefix and swap the dots for spaces, which turns
                   * `candidates.candidate.search` into "candidate search" — a schema name with the
                   * punctuation taken out is still a schema name, and it was on screen under every
                   * answer. `labelForStep` in `insight` holds the words the product actually uses,
                   * and holds them once for the chip and the citation both.
                   */
                  const label = step.label;

                  return (
                    <span
                      key={`${step.kind}:${step.id}`}
                      className={
                        step.state === 'ok'
                          ? 'tf-chip tf-copilot__step'
                          : 'tf-chip tf-copilot__step is-off'
                      }
                    >
                      {screen && step.state === 'ok' ? (
                        <Link to={screen} className="tf-link">
                          {label}
                        </Link>
                      ) : (
                        label
                      )}
                      {/*
                        The count, and it is the load-bearing half. "interviews 0" and "interviews
                        — not available" are different answers, and the whole reason this product
                        said "0 candidates are currently scheduled" about a job with three is that
                        nothing on the screen could tell those two apart.
                      */}
                      <em>
                        {step.state === 'ok'
                          ? step.found
                          : t(
                              step.state === 'refused'
                                ? 'copilot.plan.refused'
                                : 'copilot.plan.unknown',
                            )}
                      </em>
                    </span>
                  );
                })}
                {turn.plan.residue ? (
                  <span className="tf-copilot__residue">
                    {t('copilot.plan.residue', { residue: turn.plan.residue })}
                  </span>
                ) : null}
              </div>
            ) : null}

            <div className={turn.refused ? 'tf-copilot__reply tf-muted' : 'tf-copilot__reply'}>
              <AnswerText text={turn.reply} />
            </div>
            {/*
              Model contract 11 made visible. Citations name the facts the answer leaned on, by the
              same labels the record shows — so checking one is reading a line on the page, not
              opening a debugger.
            */}
            {turn.citations.length > 0 ? (
              <p className="tf-copilot__citations">
                <span className="tf-copilot__citations-label">{t('copilot.citations')}</span>
                {turn.citations.map((citation) => (
                  <span key={citation} className="tf-tag">
                    {citation}
                  </span>
                ))}
              </p>
            ) : null}
            </div>
          </article>
        ))}

        {ask.isPending ? <Spinner /> : null}
        {ask.isError ? <p className="tf-copilot__error">{t('copilot.failed')}</p> : null}
      </div>

      <footer className="tf-copilot__ask">
        {/*
          The placeholder names the layer, because the header's two buttons no longer have room to
          explain themselves. "Ask about this record" and "Ask across your book of work" are the
          same two sentences the cards carried, moved to the one place somebody is looking when the
          answer to "what will this read" matters.
        */}
        <Textarea
          rows={2}
          value={question}
          placeholder={t(layer === 'book' ? 'copilot.placeholderBook' : 'copilot.placeholder')}
          aria-label={t(layer === 'book' ? 'copilot.placeholderBook' : 'copilot.placeholder')}
          onChange={(event) => setQuestion(event.target.value)}
          onKeyDown={(event) => {
            // Enter sends, Shift+Enter breaks the line. A question is usually one line, and making
            // people reach for a button for every one of them is the difference between a tool that
            // gets used and one that gets opened twice.
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              send(question);
            }
          }}
        />
        <div className="tf-copilot__ask-row">
          <Button size="sm" disabled={ask.isPending} onClick={() => send(question)}>
            {t('copilot.send')}
          </Button>
          {/*
            Clear, beside Ask — 31 Aug 2026.

            The transcript already ends when the flyout closes, so this is not a new capability; it
            is the same one without having to close the panel and lose your place on the record.
            Disabled with nothing to clear rather than hidden: a control that appears and disappears
            is one people stop looking for.

            It clears the conversation only. The question in the box is left alone, because somebody
            who has typed half a question and wants the history gone did not ask to lose it.
          */}
          <Button
            variant="secondary"
            size="sm"
            disabled={turns.length === 0 || ask.isPending}
            onClick={() => setTurns([])}
          >
            {t('copilot.clear')}
          </Button>
        </div>
        {/* Below both, and small. It was sharing the row with Ask and taking half of it — a lot of
            a 22rem rail for a sentence that is read once and then never again. */}
        <p className="tf-muted tf-copilot__note">{t('copilot.readsOnly')}</p>
      </footer>
    </section>
  );
}

/*
 * The answer, rendered — 31 Aug 2026.
 *
 * ── Why there is a renderer at all ─────────────────────────────────────────────────────────────
 *
 * The reply was a `<p>{text}</p>`, on the reasonable assumption that a model asked for "the plain,
 * specific language a recruiter would use with a client" returns prose. It does not: it returns
 * prose *with markdown in it*. A real answer on the candidate record read
 *
 *     The strongest thing on this profile is the candidate's **12 years of total experience**
 *
 * with the asterisks on screen, and a multi-part answer came back as one unbroken paragraph because
 * its newlines were collapsed by CSS. Both are the same defect: the answer is structured and the
 * screen was throwing the structure away.
 *
 * ── A subset, not a markdown library ───────────────────────────────────────────────────────────
 *
 * Four constructs, because four is what models actually emit here: paragraphs, bullet lines, `**`
 * for emphasis, and a backtick for a value quoted verbatim. No headings — a heading inside a 22rem
 * answer is noise. No links, and that is the load-bearing omission: **a link in an answer is a
 * navigation target chosen by a model**, which is a different and much larger decision than
 * emphasis, and this product's answer to "find and do things" is the interpretation chips above,
 * whose destinations come from `STEP_SCREENS` in this file rather than from a sentence.
 *
 * Everything is built as React elements from text. Nothing reaches `dangerouslySetInnerHTML`, so
 * markup a model emits — deliberately, or because a record contained some — is text on the screen
 * and cannot become markup. That property is why this is forty lines here rather than a dependency.
 */
function AnswerText({ text }: { text: string }) {
  /*
   * Blocks are separated by a blank line; a run of `-`, `*` or `1.` lines is a list. Split on the
   * blank line first so a list immediately after a sentence is still two blocks — models write that
   * shape constantly, and a single pass over lines gets it wrong.
   */
  const blocks = text
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter((block) => block !== '');

  return (
    <>
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n').map((line) => line.trim());
        const bullets = lines.every((line) => /^([-*]|\d+\.)\s+/.test(line));

        if (bullets) {
          return (
            <ul key={blockIndex} className="tf-copilot__bullets">
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>
                  <Emphasised text={line.replace(/^([-*]|\d+\.)\s+/, '')} />
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={blockIndex}>
            {/*
              A single newline inside a block is a line the model meant to break, so it is kept —
              the alternative is the wall of text that prompted this.
            */}
            {lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 ? <br /> : null}
                <Emphasised text={line} />
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

/**
 * Emphasis and verbatim values within one line, and nothing else.
 *
 * One split with two alternatives rather than two passes: a second pass over already-rendered
 * elements would have to walk React children, and a second pass over the string would let a marker
 * inside a quoted value be re-read as emphasis. Unmatched markers are left exactly as typed — a
 * lone asterisk in a job title is an asterisk, not a syntax error.
 */
function Emphasised({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
          return <code key={index}>{part.slice(1, -1)}</code>;
        }
        return <Fragment key={index}>{part}</Fragment>;
      })}
    </>
  );
}
